<template>
  <div class="ThreeWorld">
    <canvas id="canvas" ref="canvas" class="canvas" :class="{ show: ready }"/>  
    <div class="ThreeWorld__scroll">
      <div class="ThreeWorld__container"></div>     
    </div>
    <div class="ThreeWorld__overlay"/>
    <div class="ThreeWorld__black"/>
    <div class="ThreeWorld__trigger"/>

    <BloomPass ref="bloomPass" v-if="is_loaded"/>

  </div>
</template>

<script>

import Settings from '@/config/Settings'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'

import BloomPass from '@/components/BloomPass.vue'

import gsap from "gsap"
import ScrollTrigger from "gsap/ScrollTrigger"
import MotionPathPlugin from "gsap/MotionPathPlugin"
import ScrollToPlugin from "gsap/ScrollToPlugin"

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin, ScrollToPlugin)

export default {
  name: 'ThreeWorld',
  props: ['indice'],
  components: { BloomPass },
  data: ()=>({
    ready: true,
    is_loaded: false,
    count: 0,
    cue: 0,
    old: 0,
    hidden: true,
    overlay: false,
    step: 0,
    back: 2.50,
    exposure: 1,
  }),
  beforeDestroy() {
  
  },

  mounted() {

    this.$cubes_names = ['Ambers_01', 'Ambers_02', 'Ambers_03', 'Ambers_04', 'Ambers_05', 'Ambers_06', 'Ambers_07', 'Ambers_08']

    this.$cubes = []
    this.$animations = {}
    
    this.$scene = new THREE.Scene()
    this.$clock = new THREE.Clock()

    this.$renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true })
    this.$renderer.setSize(window.innerWidth,window.innerHeight)
    this.$renderer.setPixelRatio( window.devicePixelRatio )


    this.$renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.$renderer.toneMappingExposure =  Math.pow( this.exposure, 4.0 );
    this.$renderer.outputEncoding = THREE.sRGBEncoding

    this.$ambient = new THREE.AmbientLight( 0xffffff, 1, 1 )
    this.$scene.add( this.$ambient )

    const dracoLoader = new DRACOLoader()
          dracoLoader.setDecoderPath('three/examples/js/libs/draco/gltf/')
    const loader = new GLTFLoader()
          loader.setDRACOLoader( dracoLoader )
          loader.load( 'static/'+ Settings.glb,  (glb)=> {

            this.$glb = glb
            this.$scene.add(glb.scene)
            this.$camera = glb.cameras[0]
            this.$camera.layers.enable(Settings.BLOOM_SCENE)
            this.$mixer = new THREE.AnimationMixer(glb.scene)
            this.$animations['traveling'] = this.setAction(glb.animations[0])
            this.$animations['cubes'] = this.setAction(glb.animations[1])

            glb.scene.traverse((child)=>{
                const res = this.$cubes_names.indexOf(child.name)
                if(res != -1) 
                  this.$cubes.push(glb.scene.getObjectByName(child.name, true))
            })

            this.is_loaded = true
            this.render()

        } )

    window.addEventListener( 'resize', this.onWindowResize )
      
    this.ready = true
    this.$nextTick(()=>{

      this.$scr = ScrollTrigger.create({

        trigger: ".ThreeWorld__container",
        start: "top top",
        end: "bottom 100%",
        scrub: 5,
        markers: true,

        onUpdate: (self)=>{

          this.cue = Settings.traveling_duration*self.progress.toFixed(3)

          if(this.hidden == false && this.cue < 3 ) {
            this.hidden = true
            this.hideCubes()
          }
          else if(this.hidden == true && this.cue > 3 && this.cue < 9 ) {
            this.hidden = false
            this.showCubes()
          }
          else if(this.hidden == false && this.cue > 9 ) {
            this.hidden = true
            this.hideCubes()
          }

          if(this.cue > 18 && this.cue < 21){
            const bloom = (this.cue-18)
            this.$refs.bloomPass.updateBloom(.5+bloom * .7)
          } 
          else if(this.cue < 18){ 
            this.$refs.bloomPass.updateBloom(0.5)
          }

          if(this.cue > 23 && this.cue < 29 && this.overlay == false && this.step != 2){
            this.step = 1
            this.overlay = true
            gsap.set('.ThreeWorld__overlay', { opacity: 1 })
          } 
          else if(this.cue > 29 && this.overlay == true){
            this.step = 2
            this.overlay = false
            gsap.to('.ThreeWorld__overlay', { opacity: 0, duration: .25 })
          } 
          else if(this.cue < 29 && this.cue > 23 && this.overlay == false && this.step == 2){
            this.step = 3
            this.overlay = true
            gsap.to('.ThreeWorld__overlay', { opacity: 1, duration: .25 })
          }
          else if(this.cue < 23 && this.overlay == true){
            this.step = 0
            this.overlay = false
            gsap.set('.ThreeWorld__overlay', { opacity: 0 })
          } 

          if(this.cue > 25 && this.cue < 28){
            const op = (this.cue-25)/3
            gsap.set('.ThreeWorld__black', { opacity: op })
          } 
          else if(this.cue > 28 && this.cue < 29){   
            const op1 = Math.abs(this.cue-29)
            gsap.set('.ThreeWorld__black', { opacity: op1 })
          } 

          this.playAction(this.$animations['traveling'], this.cue)
          this.playAction(this.$animations['cubes'], this.cue)

          this.render()

          // console.log(this.cue);

        }
      })

    })
  },

  methods:{
    render(){
      const delta = this.$clock.getDelta()
      this.$mixer.update(delta)

      // this.$renderer.render(this.$scene, this.$camera);

      if(this.$refs.bloomPass)
        this.$refs.bloomPass.render()
    },

    setAction(clip) {
      const action = this.$mixer.clipAction(clip)
            action.loop = THREE.LoopOnce
            action.clampWhenFinished = true
      return { clip: clip, action: action }
    },
    playAction(animation, cue) {
      animation.action.time = cue
      animation.clip.duration = animation.clip.time
      animation.action.play()
    },
    showCubes() {
      _.each(this.$cubes, (cube)=>{ cube.visible = true })
    },

    hideCubes() {
      _.each(this.$cubes, (cube)=>{ cube.visible = false })
    },
    onWindowResize() {
      this.$camera.aspect = window.innerWidth / window.innerHeight
      this.$camera.updateProjectionMatrix()
      this.$renderer.setSize( window.innerWidth, window.innerHeight )
    },
  }

}
</script>

<style lang="stylus">
@import '~@/config/Settings'

.ThreeWorld
  position fixed
  top 0
  width 100vw
  height 100%
  overflow hidden  
 
  canvas
    width 100%
    height 100%
    opacity 0
    transition opacity .5s
    transition-delay 1

    &.show 
      opacity 1

  &__scroll
    position absolute
    top 0
    width 100vw
    height 100%
    overflow-y scroll
    overflow-x hidden

  &__container
     width 100%
     position relative
     container-height(2)

  &__overlay
    position absolute
    top 0
    left 0
    width 100vw
    height 100vh
    background white
    opacity 0

  &__black
    position absolute
    top 0
    left 0
    width 100vw
    height 100vh
    background black
    opacity 0

  &__trigger
    width 80vw
    height 5px
    z-index 200
    pointer-events none
    // border-bottom 1px solid red
    position absolute
    top 65vh

</style>


